import { FormEvent, useRef, useState } from 'react';
import { brandName } from '@/mocks/homeData';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/dahg8mjp14a1h3mr6lpg';

export default function ContactSection() {
  const formRef = useRef<HTMLFormElement>(null);
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    const honey = String(formData.get('website_alt') ?? '').trim();
    if (honey) {
      setStatus('success');
      formRef.current?.reset();
      return;
    }

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'website_alt') return;
      const text = String(value);
      if (!text.trim()) return;
      params.append(key, text);
    });

    setStatus('submitting');
    setFormError('');

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null =
        null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const code = parsed?.code;
      const isSpam = /spam|form data is spam/i.test(String(serverMsg));

      if (response.ok && code === 'OK' && !isSpam) {
        setStatus('success');
        formRef.current?.reset();
      } else {
        setStatus('error');
        setFormError(isSpam ? '提交内容未通过校验,请检查后重试。' : String(serverMsg || '提交失败,请稍后重试。'));
      }
    } catch {
      setStatus('error');
      setFormError('网络异常,提交失败,请检查网络后重试。');
    }
  };

  return (
    <section id="contact" aria-labelledby="contact-title" className="scroll-mt-28 bg-background-50">
      <div className="mx-auto max-w-[1400px] px-4 py-14 md:px-6 md:py-20">
        <div className="grid grid-cols-1 overflow-hidden rounded-lg border border-background-200 lg:grid-cols-2">
          <div className="bg-primary-950 p-6 text-background-100 md:p-10">
            <p className="text-xs tracking-[0.25em] text-accent-400">CONTACT</p>
            <h2
              id="contact-title"
              className="mt-2 font-heading text-2xl font-bold text-background-50 md:text-3xl"
            >
              订阅热点速递 · 提供线索
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-background-200">
              留下邮箱,我们会在每天的热点汇总里同步给你;如果你手里有值得核实的线索,也可以在备注里说明,编辑组会主动联系。
            </p>

            <ul className="mt-8 space-y-5 text-sm">
              <li className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-background-50/10 text-xl text-accent-400">
                  <i className="ri-mail-send-line" aria-hidden="true" />
                </span>
                <span>
                  <span className="block text-background-50">投稿邮箱</span>
                  <span className="mt-1 block break-all text-background-300">tips@heibao-live.example</span>
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-background-50/10 text-xl text-accent-400">
                  <i className="ri-time-line" aria-hidden="true" />
                </span>
                <span>
                  <span className="block text-background-50">响应时间</span>
                  <span className="mt-1 block text-background-300">线索 7×24 小时接收,一般 6 小时内回复</span>
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-background-50/10 text-xl text-accent-400">
                  <i className="ri-shield-user-line" aria-hidden="true" />
                </span>
                <span>
                  <span className="block text-background-50">隐私承诺</span>
                  <span className="mt-1 block text-background-300">投稿人信息严格保密,未经同意绝不公开</span>
                </span>
              </li>
            </ul>
          </div>

          <div className="bg-background-50 p-6 md:p-10">
            {status === 'success' ? (
              <div className="flex h-full flex-col items-center justify-center gap-4 py-10 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-100 text-2xl text-primary-600">
                  <i className="ri-check-double-line" aria-hidden="true" />
                </span>
                <h3 className="font-heading text-lg font-bold text-foreground-950">订阅成功</h3>
                <p className="max-w-xs text-sm leading-relaxed text-foreground-600">
                  已收到你的信息,热点速递会在下一期汇总里同步给你。感谢关注{brandName}。
                </p>
                <button
                  type="button"
                  onClick={() => setStatus('idle')}
                  className="mt-2 cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
                >
                  再次订阅
                </button>
              </div>
            ) : (
              <form ref={formRef} data-readdy-form onSubmit={handleSubmit} noValidate>
                <h3 className="font-heading text-lg font-bold text-foreground-950">填写信息</h3>
                <p className="mt-1 text-xs text-foreground-500">带 * 的为必填项,我们不会外传你的邮箱。</p>

                <div className="mt-6 grid grid-cols-1 gap-5">
                  <div>
                    <label htmlFor="contact-name" className="mb-2 block text-sm text-foreground-800">
                      称呼
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      placeholder="怎么称呼你?"
                      className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-email" className="mb-2 block text-sm text-foreground-800">
                      邮箱 <span className="text-primary-600">*</span>
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      placeholder="you@example.com"
                      className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-topic" className="mb-2 block text-sm text-foreground-800">
                      感兴趣的方向
                    </label>
                    <select
                      id="contact-topic"
                      name="topic"
                      defaultValue="全部热点"
                      className="w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
                    >
                      <option value="全部热点">全部热点</option>
                      <option value="带货争议">带货争议</option>
                      <option value="直播圈">直播圈</option>
                      <option value="情感观察">情感观察</option>
                      <option value="平台规则">平台规则</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="contact-message" className="mb-2 block text-sm text-foreground-800">
                      备注 / 线索说明
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      rows={4}
                      maxLength={500}
                      placeholder="可简要说明线索来源与关键时间点(选填)"
                      className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                    />
                  </div>

                  <div className="bhp-field-wrap" aria-hidden="true">
                    <label htmlFor="contact-website-alt">公司网站</label>
                    <input
                      id="contact-website-alt"
                      name="website_alt"
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                  </div>
                </div>

                {status === 'error' && (
                  <p className="mt-4 flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 px-3 py-2.5 text-sm text-primary-700">
                    <i className="ri-error-warning-line mt-0.5" aria-hidden="true" />
                    {formError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="mt-6 inline-flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {status === 'submitting' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-base" aria-hidden="true" />
                      提交中…
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-fill text-base" aria-hidden="true" />
                      提交订阅
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}